import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search } from "lucide-react";
import AnimatedGradientBackground from "@/components/shared/AnimatedGradientBackground";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
      {/* Animated gradient background */}
      <AnimatedGradientBackground variant="vibrant" />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-eco-green/30"
            style={{
              left: `${10 + i * 12}%`,
              top: `${15 + (i % 4) * 20}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
        {[...Array(6)].map((_, i) => (
          <div
            key={`blue-${i}`}
            className="absolute w-3 h-3 rounded-full bg-sky-blue/20"
            style={{
              right: `${8 + i * 15}%`,
              bottom: `${25 + (i % 3) * 18}%`,
              animation: `float ${4 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 relative z-10 text-center">
        {/* 404 Number */}
        <div className="relative mb-8">
          <h1 className="text-[150px] md:text-[200px] font-bold leading-none bg-gradient-to-r from-eco-green via-primary to-sky-blue bg-clip-text text-transparent animate-fade-in">
            404
          </h1>
          <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-eco-green/20 via-primary/20 to-sky-blue/20 -z-10" />
        </div>

        {/* Message */}
        <div className="max-w-lg mx-auto mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 animate-fade-in">
            Page Not Found
          </h2>
          <p className="text-muted-foreground text-lg animate-fade-in">
            Oops! The page you're looking for doesn't exist or has been moved. 
            Let's get you back on track.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in">
          <Button asChild variant="hero" size="xl">
            <Link to="/">
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
          </Button>
          <Button asChild variant="outline" size="xl" className="border-border hover:border-primary/50">
            <Link to="/services">
              <Search className="w-5 h-5 mr-2" />
              Explore Services
            </Link>
          </Button>
        </div>

        {/* Quick Links */}
        <div className="mt-16 pt-8 border-t border-border/50 max-w-md mx-auto">
          <p className="text-sm text-muted-foreground mb-4">Or try one of these popular pages:</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
              About Us
            </Link>
            <span className="text-border">•</span>
            <Link to="/projects" className="text-muted-foreground hover:text-primary transition-colors">
              Projects
            </Link>
            <span className="text-border">•</span>
            <Link to="/sustainability" className="text-muted-foreground hover:text-primary transition-colors">
              Sustainability
            </Link>
            <span className="text-border">•</span>
            <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
